import { Flex } from "styled-system/jsx";
import { Button, Radio, RadioGroup, Select, Text } from "@ttc3k/trekker";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Days } from "shared/types";
import { formatISO } from "date-fns";
import { Collapsible, ControlledSelectRoot } from "shared/components";
import { nanoid } from "nanoid";
import {
  HoursOfOperationStatus,
  UpdateProfileFormValues,
  useHoursOfOperation,
} from "apps/directory/Operator/views";

const days = Object.values(Days);

export const HoursOfOperation = () => {
  const { t } = useTranslation();
  const { setValue, getValues, control, resetField } =
    useFormContext<UpdateProfileFormValues>();
  const hoursOfOperation = useWatch({
    control: control,
    name: "hoursOfOperation",
  });

  const {
    hoursOfOperationStatus,
    timeOptions,
    setHoursOfOperationStatus,
    getDayPath,
    getIsDayEmpty,
    get247Hours,
  } = useHoursOfOperation(hoursOfOperation);

  const toggleDay = (day: string) => {
    const dayPath = day.toLowerCase() as keyof typeof hoursOfOperation;
    const isDayEmpty = getIsDayEmpty(day);
    if (isDayEmpty) {
      setValue(
        `hoursOfOperation.${dayPath}`,
        {
          start: formatISO(new Date().setHours(9, 0, 0, 0)),
          end: formatISO(new Date().setHours(17, 0, 0, 0)),
        },
        { shouldDirty: true },
      );
    } else {
      setValue(
        `hoursOfOperation.${dayPath}`,
        {
          start: "",
          end: "",
        },
        { shouldDirty: true },
      );
    }
  };

  /**
   * Only the `hoursOfOperation.{day}.start` and `hoursOfOperation.{day}.end`
   * are registered to a controlled input field, so those fields explicitly
   * have to be reset (not the parent objects)
   */
  const resetHoursOfOperationToDefaultValue = () => {
    Object.keys(hoursOfOperation).forEach((day) => {
      const dayPath = day as keyof typeof hoursOfOperation;
      resetField(`hoursOfOperation.${dayPath}.start`);
      resetField(`hoursOfOperation.${dayPath}.end`);
    });
  };

  const handleSelectHoursOfOperationStatus = (e: {
    value: HoursOfOperationStatus;
  }) => {
    setHoursOfOperationStatus(e.value);
    if (e.value === HoursOfOperationStatus.Open247) {
      setValue("hoursOfOperation", get247Hours(), { shouldDirty: true });
    } else {
      /**
       * If switching between 24/7 and custom hours, retain
       * the default state of `hoursOfOperation` so that we don't
       * have to re-enter the hours
       */
      resetHoursOfOperationToDefaultValue();
    }
  };

  const handleSelectTimeOption = (
    timeOption: string,
    day: string,
    path: "start" | "end",
  ) => {
    const dayPath = getDayPath(day);
    setValue(`hoursOfOperation.${dayPath}.${path}`, timeOption, {
      shouldDirty: true,
    });
  };

  return (
    <>
      <RadioGroup
        value={hoursOfOperationStatus}
        onValueChange={(e: { value: HoursOfOperationStatus }) =>
          handleSelectHoursOfOperationStatus(e)
        }
      >
        <Radio value={HoursOfOperationStatus.Open247}>
          {t("directory:OPERATOR.PROFILE.HOURS_OF_OPERATION.OPEN_247")}
        </Radio>
        <Radio value={HoursOfOperationStatus.Custom}>
          {t("directory:OPERATOR.PROFILE.HOURS_OF_OPERATION.CUSTOM")}
        </Radio>
      </RadioGroup>{" "}
      <Collapsible
        open={hoursOfOperationStatus === HoursOfOperationStatus.Custom}
      >
        <Flex flexDir={"column"} gap={"300"}>
          <Flex gap={"100"}>
            {days.map((day) => (
              <Button
                key={nanoid()}
                type={"button"}
                visual={getIsDayEmpty(day) ? "primary" : "accent.blue"}
                mode={getIsDayEmpty(day) ? "light" : "dark"}
                flexBasis={1}
                flexGrow={1}
                w={"full"}
                size={"sm"}
                onClick={() => toggleDay(day)}
              >
                {t(`core:LOOKUPS.DAYS.${day}_FULL`)}
              </Button>
            ))}
          </Flex>
          {days.map((day, i) =>
            !getIsDayEmpty(day) ? (
              <Flex key={i} gap={"150"} alignItems={"center"}>
                <Text minW={"100px"}>{t(`core:LOOKUPS.DAYS.${day}_FULL`)}</Text>
                <ControlledSelectRoot
                  name={`hoursOfOperation.${getDayPath(day)}.start`}
                  items={timeOptions}
                  value={[
                    getValues(`hoursOfOperation.${getDayPath(day)}.start`),
                  ]}
                  onValueChange={({ value }: { value: string[] }) =>
                    handleSelectTimeOption(value[0], day, "start")
                  }
                >
                  {timeOptions.map((timeOpt) => (
                    <Select.Item key={timeOpt.value} item={timeOpt} />
                  ))}
                </ControlledSelectRoot>
                {t(`core:TO`)}
                <ControlledSelectRoot
                  name={`hoursOfOperation.${getDayPath(day)}.end`}
                  items={timeOptions}
                  value={[getValues(`hoursOfOperation.${getDayPath(day)}.end`)]}
                  onValueChange={({ value }: { value: string[] }) =>
                    handleSelectTimeOption(value[0], day, "end")
                  }
                >
                  {timeOptions.map((timeOpt) => (
                    <Select.Item key={timeOpt.value} item={timeOpt} />
                  ))}
                </ControlledSelectRoot>
              </Flex>
            ) : null,
          )}
        </Flex>
      </Collapsible>
    </>
  );
};
